import { useQuery } from "@tanstack/react-query";
import apiClient from "../constants/apiClient";

export interface GatewayDeviceStatus {
    state: string;
    last_ok?: string | null;
    error?: string | null;
    pingable?: boolean | null;
}

export interface GatewayHeartbeat {
    ts?: string;
    uptime_s?: number;
    [key: string]: unknown;
}

export interface GatewayMeasurement {
    id: number;
    value: number;
    created?: string;
}

export interface GatewayStatusResponse {
    // Keyed by device ip address (AVOS broker) or room id (RipeWise)
    status: Record<string, GatewayDeviceStatus> | null;
    // Free-form diagnostics payload (shape may vary)
    diagnostics: Record<string, unknown> | null;
    heartbeat: GatewayHeartbeat | null;
    measurements: GatewayMeasurement[] | null;
    last_updated: string | null;
    // "mqtt" (AVOS broker cache) or "ripewise" (ripening program service)
    source?: string | null;
}

/**
 * Poll the latest cached gateway status (status / diagnostics / heartbeat / measurements).
 *
 * The backend serves data populated from retained MQTT messages, so a single
 * lightweight poll drives live updates for every section on the page.
 */
export function useGatewayStatus(clientId: string | undefined, refetchInterval = 3000) {
    return useQuery<GatewayStatusResponse>({
        queryKey: ["gatewayStatus", clientId],
        queryFn: async () => {
            if (!clientId) {
                return {
                    status: null,
                    diagnostics: null,
                    heartbeat: null,
                    measurements: null,
                    last_updated: null,
                };
            }
            const response = await apiClient.get(`/device/${clientId}/gateway/status`);
            return response.data;
        },
        enabled: !!clientId,
        refetchInterval,
    });
}
